import { baseKeymap, toggleMark } from "prosemirror-commands";
import { dropCursor } from "prosemirror-dropcursor";
import { gapCursor } from "prosemirror-gapcursor";
import { history, redo, undo } from "prosemirror-history";
import { ellipsis, emDash, inputRules, smartQuotes } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import { type MenuElement } from "prosemirror-menu";
import type { Command } from "prosemirror-state";

import { schema } from "../adapter/schema";
import {
  backspaceCommand,
  enterCommand,
  shiftTabCommand,
  spaceCommand,
  tabCommand,
} from "../commands";
import {
  alignCenterIconSpec,
  alignLeftIconSpec,
  alignRightIconSpec,
  boldIconSpec,
  bulletListIconSpec,
  codeBlockIconSpec,
  italicIconSpec,
  numberedListIconSpec,
  quoteIconSpec,
  underlineIconSpec,
} from "../icons";
import { alignmentItems } from "../toolbar/alignment";
import { toggleBlockTypeItem } from "../toolbar/block-toggle";
import { formulaItem } from "../toolbar/formula";
import { imageItem } from "../toolbar/image";
import { indentDecreaseItem, indentIncreaseItem } from "../toolbar/indent";
import { linkItem } from "../toolbar/link";
import { markItem } from "../toolbar/mark-toggle";
import { createCloseItemForPanel, createMoreOptionsItem } from "../toolbar/panel-navigation";
import { textColorItems, textColorPanelItem } from "../toolbar/text-color-panel";
import { textStyleItems, textStylePanelItem } from "../toolbar/text-style-panel";
import { isFormulaSelected, isImageSelected, isSelectionInCodeBlock, modKey } from "../utils";

import { codeBlockMergePlugin } from "./code-block-merge";
import { type PanelContent, type PanelType } from "./panel";
import { toolbarPlugin, toolbarPluginKey } from "./toolbar";

import { t } from "i18n/i18n";

function buildInputRules() {
  const rules = smartQuotes.concat(ellipsis, emDash);
  return inputRules({ rules });
}

/**
 * Build keyboard shortcuts for the editor.
 * "Mod" is a platform-agnostic modifier key (Cmd on Mac, Ctrl on Windows/Linux)
 */
function buildKeymap(): Record<string, Command> {
  const keys: Record<string, Command> = {};

  // History shortcuts: undo and redo
  keys["Mod-z"] = undo; // Cmd/Ctrl+Z
  keys["Shift-Mod-z"] = redo; // Cmd/Ctrl+Shift+Z

  // Mark shortcuts: toggle formatting with keyboard
  if (schema.marks.bold) {
    const toggleBold = (state, dispatch) =>
      isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state)
        ? false
        : toggleMark(schema.marks.bold)(state, dispatch);
    keys["Mod-b"] = toggleBold;
    keys["Mod-B"] = toggleBold;
  }
  if (schema.marks.italic) {
    const toggleItalic = (state, dispatch) =>
      isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state)
        ? false
        : toggleMark(schema.marks.italic)(state, dispatch);
    keys["Mod-i"] = toggleItalic;
    keys["Mod-I"] = toggleItalic;
  }
  if (schema.marks.underline) {
    const toggleUnderline = (state, dispatch) =>
      isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state)
        ? false
        : toggleMark(schema.marks.underline)(state, dispatch);
    keys["Mod-u"] = toggleUnderline;
    keys["Mod-U"] = toggleUnderline;
  }

  keys.Tab = tabCommand;
  keys["Shift-Tab"] = shiftTabCommand;

  // Alt+F10: standard rich-text-editor shortcut to move focus to the toolbar.
  keys["Alt-F10"] = (state, dispatch) => {
    dispatch?.(state.tr.setMeta(toolbarPluginKey, { shouldFocus: true }));
    return true;
  };

  // Escape unfocuses the editor so Tab can resume normal keyboard navigation.
  keys.Escape = (_state, _dispatch, view) => {
    view?.dom.blur();
    return true;
  };

  // custom handling for list items
  keys.Enter = enterCommand;
  keys.Space = spaceCommand;
  for (const bsKey of ["Backspace", "Mod-Backspace", "Alt-Backspace", "Shift-Backspace"]) {
    keys[bsKey] = backspaceCommand;
  }

  return keys;
}

/**
 * Menu item creation helpers
 *
 * These functions create menu items for the toolbar, each performing a schema check
 * and returning the appropriate menu element or null if the schema doesn't support it.
 */

function createTextStylePanelItem(): MenuElement | null {
  if (!schema.nodes.heading || !schema.nodes.paragraph) return null;
  return textStylePanelItem();
}

function createTextStyleItems(): MenuElement[] {
  if (!schema.nodes.heading || !schema.nodes.paragraph) return [];
  return textStyleItems();
}

function createBoldItem(): MenuElement | null {
  if (!schema.marks.bold) return null;

  return markItem(schema.marks.bold, {
    title: `${t("worksheet.rich_text_editor.toggle_bold")} (${modKey}+B)`,
    icon: boldIconSpec,
  });
}

function createItalicItem(): MenuElement | null {
  if (!schema.marks.italic) return null;

  return markItem(schema.marks.italic, {
    title: `${t("worksheet.rich_text_editor.toggle_italic")} (${modKey}+I)`,
    icon: italicIconSpec,
  });
}

function createUnderlineItem(): MenuElement | null {
  if (!schema.marks.underline) return null;

  return markItem(schema.marks.underline, {
    title: `${t("worksheet.rich_text_editor.toggle_underline")} (${modKey}+U)`,
    icon: underlineIconSpec,
  });
}

function createLinkItem(): MenuElement | null {
  if (!schema.marks.link) return null;
  return linkItem();
}

function createImageItem(): MenuElement | null {
  if (!schema.nodes.image) return null;
  return imageItem();
}

function createFormulaItem(): MenuElement | null {
  if (!schema.nodes.formula) return null;
  return formulaItem();
}

function createQuoteItem(): MenuElement | null {
  if (!schema.nodes.quote) return null;

  return toggleBlockTypeItem(schema.nodes.quote, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.toggle_quote"),
    icon: quoteIconSpec,
  });
}

function createCodeBlockItem(): MenuElement | null {
  if (!schema.nodes.code_block) return null;

  return toggleBlockTypeItem(schema.nodes.code_block, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.toggle_code_block"),
    icon: codeBlockIconSpec,
  });
}

function createOrderedListItem(): MenuElement | null {
  if (!schema.nodes.ordered_list_item) return null;

  return toggleBlockTypeItem(schema.nodes.ordered_list_item, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.ordered_list_item"),
    icon: numberedListIconSpec,
  });
}

function createBulletListItem(): MenuElement | null {
  if (!schema.nodes.bullet_list_item) return null;

  return toggleBlockTypeItem(schema.nodes.bullet_list_item, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.bullet_list_item"),
    icon: bulletListIconSpec,
  });
}

function createIndentIncreaseItem(): MenuElement | null {
  return indentIncreaseItem;
}

function createIndentDecreaseItem(): MenuElement | null {
  return indentDecreaseItem;
}

function createTextColorPanelItem(): MenuElement | null {
  if (!schema.marks.color) return null;
  return textColorPanelItem();
}

function createTextColorItems(): MenuElement[] {
  if (!schema.marks.color) return [];
  return textColorItems();
}

function createAlignmentItems(): MenuElement[] {
  return alignmentItems([
    {
      value: "left",
      icon: alignLeftIconSpec,
      title: t("worksheet.rich_text_editor.align_left"),
    },
    {
      value: "center",
      icon: alignCenterIconSpec,
      title: t("worksheet.rich_text_editor.align_center"),
    },
    {
      value: "right",
      icon: alignRightIconSpec,
      title: t("worksheet.rich_text_editor.align_right"),
    },
  ]);
}

function buildGroup(...creators: Array<() => MenuElement | MenuElement[] | null>): MenuElement[] {
  // Creators may return a single item, an array of items, or null.
  return creators.flatMap((creator) => creator() ?? []);
}

/**
 * Items are organized into six groups across four panels:
 * - "main" panel: groups 1 and 2
 * - "moreOptions" panel: groups 3 and 4
 * - "textStyle" panel: group 5
 * - "textColor" panel: group 6
 */
function buildToolbar(): Record<PanelType, PanelContent> {
  return {
    main: [
      // Group 1: more options button + Text style, Bold, Italic, Underline
      [
        createMoreOptionsItem,
        ...buildGroup(
          createTextStylePanelItem,
          createBoldItem,
          createItalicItem,
          createUnderlineItem,
        ),
      ],
      // Group 2: Bullet list, Numbered list, Indent, Outdent, Text color
      buildGroup(
        createBulletListItem,
        createOrderedListItem,
        createIndentIncreaseItem,
        createIndentDecreaseItem,
        createTextColorPanelItem,
      ),
    ],
    moreOptions: [
      // Group 3: close button + Link, Image, Quote, Code block, Formula
      [
        createCloseItemForPanel("moreOptions"),
        ...buildGroup(
          createLinkItem,
          createImageItem,
          createQuoteItem,
          createCodeBlockItem,
          createFormulaItem,
        ),
      ],
      // Group 4: Align left, Align center, Align right
      buildGroup(createAlignmentItems),
    ],
    textStyle: [
      // Group 5: Text style panel — close button + Normal + H1–H6
      [createCloseItemForPanel("textStyle"), ...createTextStyleItems()],
    ],
    textColor: [
      // Group 6: Text color panel — close button + text color options
      [createCloseItemForPanel("textColor"), ...createTextColorItems()],
    ],
  };
}

/** Assembles the editor's plugin configuration: input rules, keyboard shortcuts, core behaviors, and optionally the toolbar. */
export function createBasePlugins(showToolbar: boolean) {
  const plugins = [
    buildInputRules(), // auto-transforms text as you type: smart quotes, em dashes, ellipsis
    keymap(buildKeymap()), // Cmd+B bold, Cmd+Z undo, etc.
    keymap(baseKeymap), // Enter, Backspace, arrow keys
    dropCursor(), // visual indicator when dragging content
    gapCursor(), // click into gaps between block nodes
    history(), // undo/redo
    codeBlockMergePlugin(), // merge adjacent code blocks after a transaction
  ];

  if (showToolbar) {
    plugins.push(toolbarPlugin({ content: buildToolbar() }));
  }

  return plugins;
}
